import { Box, Divider, useMediaQuery } from '@mui/material';
import { AvailableSpecHomesFilters } from './available-spec-homes-filters';
import { useContext, useMemo, useState } from 'react';
import { IResponse, ISortDirection, ISpec } from '../../../models';
import { useQuery } from 'react-query';
import { getSpecs } from '../../../fetch';
import { MobileTable, Pagination, Table, CSVDownloadButton } from '../../../components';
import { SalesContext, update } from '../../../context/sales';
import { SALES_PORTAL_PER_PAGE_OPTIONS, getEnvironment } from '../../../helpers';

const PAGE_NAME = 'reports-available-spec-homes';
export const AvailableSpecHomesTab = () => {
  const isMobile = useMediaQuery('(max-width: 960px)');

  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const { perPageSalesPortalValues, dispatchPerPage } = useContext(SalesContext);
  const currentPageState = perPageSalesPortalValues.find(
    state => state?.pageName === PAGE_NAME
  )?.itemsPerPage;
  const perPage = useMemo<number>(
    () => (currentPageState && currentPageState >= 25 ? currentPageState : 25),
    [currentPageState]
  );

  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);

  const [filters, setFilters] = useState<{
    neighborhoodId?: string | number;
    planId?: string | number;
    territoryId?: string | number;
  } | null>({});
  const [selectedSort, setSelectedSort] = useState<string>('Territory');
  const [sortDirection, setSortDirection] = useState<{
    Territory?: ISortDirection;
    NeighborhoodDescription?: ISortDirection;
    LotNumber?: ISortDirection;
    Plan?: ISortDirection;
    Age?: ISortDirection;
  }>({
    Territory: 'Asc'
  });

  const [selectedTerritoryId, setSelectedTerritoryId] = useState<string | number>('');
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | number>('');
  const [selectedPlanId, setSelectedPlanId] = useState<string | number>('');

  const { isLoading, data: specsData } = useQuery<IResponse<ISpec[]>, Error>(
    ['getSpecs', page, perPage, filters, selectedSort, sortDirection],
    () =>
      getSpecs({
        page: page + 1,
        perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        neighborhoodId: selectedNeighborhoodId as string,
        territoryId: selectedTerritoryId,
        planId: selectedPlanId
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  const data = useMemo(() => specsData?.records ?? [], [specsData]);

  const columns = useMemo(() => {
    const handleClickColumn = (column: string) => {
      setSelectedSort(column);
      setSortDirection({
        ...sortDirection,
        [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
      });
    };
    return [
      {
        Header: 'Territory',
        accessor: 'territory',
        isServerSorted: selectedSort === 'Territory',
        isServerSortedDesc: sortDirection.Territory === 'Desc',
        handleClickColumn: () => handleClickColumn('Territory')
      },
      {
        Header: 'Neighborhood',
        accessor: 'neighborhoodDescription',
        isServerSorted: selectedSort === 'NeighborhoodDescription',
        isServerSortedDesc: sortDirection.NeighborhoodDescription === 'Desc',
        handleClickColumn: () => handleClickColumn('NeighborhoodDescription')
      },
      {
        Header: 'Lot',
        accessor: 'lotNumber',
        isServerSorted: selectedSort === 'LotNumber',
        isServerSortedDesc: sortDirection.LotNumber === 'Desc',
        handleClickColumn: () => handleClickColumn('LotNumber')
      },
      {
        Header: 'Plan',
        accessor: 'plan',
        isServerSorted: selectedSort === 'Plan',
        isServerSortedDesc: sortDirection.Plan === 'Desc',
        handleClickColumn: () => handleClickColumn('Plan')
      },
      {
        Header: 'Age (Days)',
        accessor: 'age',
        isServerSorted: selectedSort === 'Age',
        isServerSortedDesc: sortDirection.Age === 'Desc',
        handleClickColumn: () => handleClickColumn('Age')
      }
    ];
  }, [sortDirection, selectedSort]);

  return (
    <>
      <Box mt={1}>
        <AvailableSpecHomesFilters
          isLoading={isLoading}
          setHasAppliedFilters={setHasAppliedFilters}
          hasAppliedFilters={hasAppliedFilters}
          setSelectedTerritoryId={setSelectedTerritoryId}
          selectedTerritoryId={selectedTerritoryId}
          setSelectedNeighborhoodId={setSelectedNeighborhoodId}
          selectedNeighborhoodId={selectedNeighborhoodId}
          setSelectedPlanId={setSelectedPlanId}
          selectedPlanId={selectedPlanId}
          applyFilters={(clearFilters?: boolean) => {
            if (clearFilters) {
              setFilters({ territoryId: undefined, neighborhoodId: undefined, planId: undefined });
            } else {
              setPage(0);
              setFilters({
                ...filters,
                territoryId: selectedTerritoryId,
                neighborhoodId: selectedNeighborhoodId,
                planId: selectedPlanId
              });
            }
          }}
        />
        <Box justifyContent="flex-end" mb={1} display="flex">
          <CSVDownloadButton
            name="available-spec-homes-csv"
            fileNamePrefix="availableSpecHomesReport"
            columns={[
              {
                header: 'Territory',
                key: 'territory'
              },
              { header: 'Neighborhood', key: 'neighborhoodDescription' },
              { header: 'Lot', key: 'lotNumber' },
              { header: 'Plan', key: 'plan' },
              { header: 'Age (Days)', key: 'age' }
            ]}
            apiRequest={() =>
              getSpecs({
                page: 1,
                perPage: null,
                sortBy: selectedSort,
                sortDirection: sortDirection[selectedSort] as string,
                neighborhoodId: selectedNeighborhoodId as string,
                territoryId: selectedTerritoryId,
                planId: selectedPlanId
              })
            }
            transformResponse={(res: IResponse<ISpec[]>) => res?.records}
          >
            CSV
          </CSVDownloadButton>
        </Box>
        <Divider />
        <Table
          stickyHeaders
          columns={columns}
          data={data}
          isLoading={isLoading}
          hidePagination
          mobileProps={{
            fields: columns
              .filter(c => c.Header)
              .map(c => ({
                name: c.Header,
                accessor: c.accessor
              })),
            mobileDefaultAccessor: 'neighborhoodDescription'
          }}
          ResponsiveComponent={isMobile ? MobileTable : undefined}
        />
        {!isLoading && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            rowsPerPageOptions={SALES_PORTAL_PER_PAGE_OPTIONS}
            setRowsPerPage={itemsPerPage => {
              return dispatchPerPage(
                update({
                  pageName: PAGE_NAME,
                  environment: getEnvironment(),
                  itemsPerPage: itemsPerPage
                })
              );
            }}
          />
        )}
      </Box>
    </>
  );
};
